import { ToastProvider } from '@roots/ui';
import * as Sentry from '@sentry/react';
// App entry — Vite + React 19, TanStack Query/Router, PWA registration, Sentry (no PII).
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import { AuthProvider } from './lib/auth.js';
import { startOfflineSync } from './lib/sync.js';
import { router } from './router.js';

if (import.meta.env.VITE_SENTRY_DSN_WEB) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN_WEB,
    environment: import.meta.env.MODE,
    sendDefaultPii: false, // PRD §10 — errors only, no behavioural analytics
    integrations: [],
    tracesSampleRate: 0,
  });
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) => {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return count < 3; // API §6 — 3 retries with backoff
      },
      retryDelay: (attempt) => 1000 * 2 ** attempt,
      refetchOnWindowFocus: false,
    },
  },
});

startOfflineSync(queryClient);

// The service worker posts this when a push arrives while the app is open (sw.ts).
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if ((event.data as { type?: string } | undefined)?.type !== 'roots:push') return;
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    void queryClient.invalidateQueries({ queryKey: ['home'] });
  });
}

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { type: 'module' });
  });
}

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
